<template>
  <div class="form-value-item d-flex flex-column gap-2">
    <label v-if="getValueLabelCaption" :for="`edit-input-${element.id}`">
      {{ getValueLabelCaption }}
    </label>
    <input
      v-if="isFieldVisible('simpleInput')"
      ref="inputRef"
      class="w-100 fw-normal"
      :id="`edit-input-${element.id}`"
      :value="displayValue"
      :type="getInputType"
      :step="getInputStep"
      :required="isRequired"
      :pattern="getPattern"
      :min="getElementMinMax('min')"
      :max="getElementMinMax('max')"
      :disabled="isDisabled"
      @input="onHandleInput"
      @change="onHandleChange"
      @keydown="onHandleKeyDown"
      @invalid="onHandleInvalid"
    />
    <textarea
      v-else-if="isFieldVisible('textarea')"
      :id="`edit-input-${element.id}`"
      v-model="inputValue"
      class="w-100 fw-normal"
      :required="isRequired"
      :disabled="isDisabled"
      @input="onHandleInput"
      @invalid="onHandleInvalid"
    />
  </div>
</template>

<script>
import {
  getFormatNumber,
  showInputInvalidCustomMessage,
} from '../../utils/helpers';
import FilePicker from '../core/FilePicker.vue';
export default {
  data() {
    return {};
  },
  props: {
    element: Object,
    value: {
      type: [String, Number],
      default: '',
    },
    isUserHasAccess: { type: Boolean, default: true },
  },
  methods: {
    onHandleInput(event) {
      showInputInvalidCustomMessage(event);
      const inputElement = event.target;
      if (this.isFieldVisible('simpleInput')) {
        if (this.element.type === 'file') {
          return;
        }
        if (this.element.type === 'formatted_number') {
          const originalCursorPos = inputElement.selectionStart;
          const digitsBeforeCursor = inputElement.value
            .slice(0, originalCursorPos)
            .replace(/\D/g, '').length;
          let rawValue = inputElement.value
            .replace(
              new RegExp(`\\${this.element?.settings?.thSep || '.'}`, 'g'),
              ''
            )
            .replace(this.element?.settings?.decSep || ',', '.')
            .replace(/[^0-9.]/g, '');
          if (event.key === 'ArrowUp') {
            rawValue = parseFloat(rawValue) + 1;
          } else if (event.key === 'ArrowDown') {
            rawValue = parseFloat(rawValue) - 1;
          }
          this.inputValue = rawValue;
          this.$nextTick(() => {
            let digitCount = 0;
            let newCursorPos = inputElement.value.length;
            for (let i = 0; i < inputElement.value.length; i++) {
              if (/\d/.test(inputElement.value[i])) {
                digitCount++;
              }
              if (digitCount === digitsBeforeCursor) {
                newCursorPos = i + 1;
                break;
              }
            }
            inputElement.setSelectionRange(newCursorPos, newCursorPos);
          });
        } else {
          this.inputValue = inputElement.value;
        }
      }
    },
    onHandleChange(event) {
      if (this.element.type === 'file') {
        this.element.files = event.target.files;

        setTimeout(() => {
          this.$emit('onFileChange');
        }, 100);
      }
    },
    onHandleKeyDown(event) {
      if (this.element.type === 'formatted_number') {
        const allowedKeys = [
          'Backspace',
          'ArrowLeft',
          'ArrowRight',
          'Tab',
          'Delete',
          'Shift',
          'Control',
        ];
        if (['ArrowUp', 'ArrowDown'].includes(event.key)) {
          event.preventDefault();
          this.onHandleInput(event);
        } else if (
          allowedKeys.includes(event.key) ||
          event?.ctrlKey ||
          event?.shiftKey ||
          event?.metaKey ||
          /^[0-9]$/.test(event.key)
        ) {
          return;
        }
        event.preventDefault();
      }
    },
    onHandleInvalid(event) {
      if (this.getPattern && this.element.settings?.errorMessage) {
        if (event.target?.validity?.patternMismatch) {
          event.target.setCustomValidity(this.element.settings?.errorMessage);
        } else {
          event.target.setCustomValidity('');
        }
      } else {
        showInputInvalidCustomMessage(event);
      }
    },
    setFileInput() {
      this.$nextTick(() => {
        if (this.element.type === 'file') {
          if (this.element.files?.length) {
            const inputElement = this.$refs.inputRef;
            inputElement.files = this.element.files;
            this.$emit('onFileChange');
          } else {
            this.$set(this.element, 'files', {});
          }
        }
      });
    },
    isFieldVisible(fieldName) {
      if (fieldName === 'simpleInput') {
        return [
          'text',
          'number',
          'formatted_number',
          'email',
          'file',
          'date',
          'time',
          'datetime-local',
          'hidden',
          'model',
        ].includes(this.element.type);
      } else if (fieldName === 'textarea') {
        return ['textarea', 'html'].includes(this.element.type);
      }
    },
    getElementMinMax(type) {
      if (
        ['range', 'number'].includes(this.element.type) &&
        this.element.settings.hasOwnProperty(type) &&
        this.element.settings[type]
      ) {
        return Number(this.element.settings[type]);
      }
      return null;
    },
  },
  computed: {
    getValueLabelCaption() {
      let captions = {
        file: 'Datei',
        filenew: 'Datei New',
        email: 'E-Mail Adresse',
        number: 'Zahl',
        formatted_number: 'Zahl',
        model: 'Model',
        range: 'Zahl',
        timestampms: 'Zeitstempel',
      };
      if (
        [
          'textarea',
          'text',
          'hidden',
          'html',
          'date',
          'time',
          'datetime-local',
          'html',
        ].includes(this.element.type)
      )
        return 'Beispiel';
      return captions[this.element.type] || null;
    },
    inputValue: {
      get() {
        return this.value;
      },
      set(val) {
        this.$emit('input', val);
      },
    },
    getInputType() {
      return ['formatted_number', 'hidden'].includes(this.element.type)
        ? 'text'
        : this.element.type;
    },
    isRequired() {
      return this.element.settings.required || false;
    },
    isDisabled() {
      return this.element.settings.disabled || !this.isUserHasAccess || false;
    },
    displayValue() {
      return this.element.type === 'formatted_number'
        ? getFormatNumber(
            this.inputValue,
            this.element?.settings?.thSep,
            this.element?.settings?.decSep,
            this.element?.settings?.toFixed
          )
        : this.inputValue;
    },
    getPattern() {
      if (this.element.type === 'text' && this.element?.settings?.pattern) {
        return `${this.element.settings.pattern}`;
      }
      return null;
    },
    getInputStep() {
      if (this.element.type !== 'number') {
        return null;
      }
      const configuredStep = this.element?.settings?.step;
      if (
        configuredStep === null ||
        configuredStep === undefined ||
        configuredStep === ''
      ) {
        return 'any';
      }
      const parsedStep = Number(configuredStep);
      return Number.isFinite(parsedStep) && parsedStep > 0 ? parsedStep : 'any';
    },
  },
  mounted() {
    this.setFileInput();
  },
  components: { FilePicker },
};
</script>

<style>
.form-value-item label {
  font-size: 13px;
  font-family: Open Sans, open-sans, sans-serif;
  color: #464d5d;
}

.form-value-item textarea,
.form-value-item input {
  padding: 9px 12px;
  border: 1px solid #dee3e9;
  color: #464d5d !important;
  border-radius: 2px;
  max-width: 100%;
  height: auto;
  font-size: 14px;
  line-height: 20px;
}
.form-value-item textarea {
  height: 200px;
  resize: none;
  overflow: scroll;
}
</style>
